import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  InputAdornment
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';

import { Despesa } from '../../types';
import CaixaFinanceiroSelect from '../selects/CaixaFinanceiroSelect';
import FormaPgtoSelect from '../selects/FormaPgtoSelect';
import { formatarValor, formatarValorOnInput, parsearMoeda } from '../../utils/formatToView';

interface DespesasPaymentFormData {
  data_pagamento: string;
  valor_pago: number;
  forma_pgto: string;
  caixa_financeiro_id: number | null;
  observacoes?: string;
}

interface DespesasPaymentFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (paymentData: DespesasPaymentFormData) => void;
  despesa: Despesa | null;
}

const DespesasPaymentForm: React.FC<DespesasPaymentFormProps> = ({
  open,
  onClose,
  onSuccess,
  despesa
}) => {
  const [formData, setFormData] = useState<DespesasPaymentFormData>({
    data_pagamento: new Date().toISOString().split('T')[0],
    valor_pago: 0,
    forma_pgto: 'dinheiro',
    caixa_financeiro_id: null,
    observacoes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [valorPagoFormatado, setValorPagoFormatado] = useState('0,00');

  // Resetar formulário quando o modal abrir
  useEffect(() => {
    if (open && despesa) {
      setFormData({
        data_pagamento: new Date().toISOString().split('T')[0],
        valor_pago: Number(despesa.valor) || 0,
        forma_pgto: 'dinheiro',
        caixa_financeiro_id: null,
        observacoes: ''
      });
      setErrors({});
      
      // Inicializar valor formatado
      const valorInicial = Number(despesa.valor) || 0;
      setValorPagoFormatado(valorInicial.toLocaleString('pt-BR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }));
    }
  }, [open, despesa]);

  const handleInputChange = (field: keyof DespesasPaymentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleValorPagoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    setValorPagoFormatado(valorFormatado);
    const valorNumerico = parsearMoeda(valorFormatado);
    handleInputChange('valor_pago', valorNumerico);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.data_pagamento) {
      newErrors.data_pagamento = 'Data do pagamento é obrigatória';
    }

    if (!formData.valor_pago || formData.valor_pago <= 0) {
      newErrors.valor_pago = 'Valor pago deve ser maior que zero';
    }

    if (!formData.forma_pgto) {
      newErrors.forma_pgto = 'Forma de pagamento é obrigatória';
    }

    if (!formData.caixa_financeiro_id) {
      newErrors.caixa_financeiro_id = 'Caixa financeiro é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSuccess(formData);
      onClose();
    } catch (error) {
      console.error('Erro ao processar pagamento da despesa:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!despesa) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" component="div">
            Marcar Despesa como Paga
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {despesa.descricao}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0.5 }}>
        <Grid container spacing={2} sx={{ p: 1 }}>
          {/* Caixa Financeiro */}
          <Grid item xs={12}>
            <CaixaFinanceiroSelect
              value={formData.caixa_financeiro_id}
              onChange={(caixaId) => handleInputChange('caixa_financeiro_id', caixaId)}
              error={!!errors.caixa_financeiro_id}
              helperText={errors.caixa_financeiro_id}
              size="small"
              fullWidth
              required
              //escritorioId={despesa.escritorio_id}
              label="Caixa Financeiro"
            />
          </Grid>

          {/* Valor da Despesa (somente leitura) */}
          <Grid item xs={12}>
            <TextField
              label="Valor da Despesa"
              value={formatarValor(Number(despesa.valor))}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  color: 'text.secondary',
                  fontWeight: 500
                }
              }}
            />
          </Grid>

          {/* Valor Pago */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Valor Pago"
              fullWidth
              size="small"
              value={valorPagoFormatado}
              onChange={(e) => handleValorPagoChange(e.target.value)}
              error={!!errors.valor_pago}
              helperText={errors.valor_pago}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          </Grid>

          {/* Data do Pagamento */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Data do Pagamento"
              type="date"
              value={formData.data_pagamento}
              onChange={(e) => handleInputChange('data_pagamento', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.data_pagamento}
              helperText={errors.data_pagamento}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Forma de Pagamento */}
          <Grid item xs={12}>
            <FormaPgtoSelect
              value={formData.forma_pgto}
              onChange={(value) => handleInputChange('forma_pgto', value)}
              label="Forma de Pagamento"
              fullWidth
              size="small"
              error={!!errors.forma_pgto}
              helperText={errors.forma_pgto}
            />
          </Grid>

          {/* Observações */}
          <Grid item xs={12}>
            <TextField
              label="Observações"
              multiline
              rows={3}
              value={formData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
              fullWidth
              size="small"
              placeholder="Observações sobre o pagamento da despesa (opcional)"
              inputProps={{
                maxLength: 500
              }}
            />
          </Grid>
        </Grid>

        {/* Alert se valor pago for diferente do valor da despesa */}
        {formData.valor_pago > 0 && Number(formData.valor_pago).toFixed(2) !== Number(despesa.valor).toFixed(2) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Valor diferente:</strong> O valor pago ({formatarValor(formData.valor_pago)})
              é diferente do valor da despesa ({formatarValor(Number(despesa.valor))}).
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={<PaymentIcon />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Processando...' : 'Confirmar Pagamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DespesasPaymentForm;
